import PropTypes from 'prop-types'
import { cn } from '../../lib/cn'

/**
 * Textarea component with label and error state
 *
 * @component
 * @example
 * <Textarea
 *   label="Message"
 *   value={message}
 *   onChange={handleChange}
 *   rows={4}
 * />
 */
export default function Textarea({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  disabled = false,
  required = false,
  rows = 4,
  maxLength,
  showCount = false,
  className = '',
  ...props
}) {
  const textareaId = name || label?.toLowerCase().replace(/\s+/g, '-')

  const textareaClasses = cn(
    'block w-full rounded-lg border px-4 py-2.5 text-base transition-colors resize-y',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500',
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
    className
  )

  const currentLength = value?.length || 0

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={textareaId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}

      <textarea
        id={textareaId}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        rows={rows}
        maxLength={maxLength}
        className={textareaClasses}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${textareaId}-error` : undefined}
        {...props}
      />

      <div className="mt-1 flex justify-between items-center">
        {error ? (
          <p
            id={`${textareaId}-error`}
            className="text-sm text-red-600"
            role="alert"
          >
            {error}
          </p>
        ) : <span />}

        {showCount && maxLength && (
          <span className={cn(
            'text-sm',
            currentLength > maxLength * 0.9 ? 'text-warning' : 'text-gray-500'
          )}>
            {currentLength} / {maxLength}
          </span>
        )}
      </div>
    </div>
  )
}

Textarea.propTypes = {
  /** Textarea label */
  label: PropTypes.string,
  /** Textarea name attribute */
  name: PropTypes.string,
  /** Textarea value */
  value: PropTypes.string,
  /** Change handler */
  onChange: PropTypes.func,
  /** Blur handler */
  onBlur: PropTypes.func,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Error message */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Required field */
  required: PropTypes.bool,
  /** Number of rows */
  rows: PropTypes.number,
  /** Maximum length */
  maxLength: PropTypes.number,
  /** Show character count */
  showCount: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string
}
